import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import type { Appointment } from '../utils/appointmentManager';
import type { CustomerPickupBusinessPrefs } from '../types/customerPickupSettings';
import { getCustomerQueueStatusLine } from '../utils/customerVisitDisplay';

interface CustomerQueueStatusCardProps {
  appointment: Appointment;
  /** When set, visit line can show "In progress" / "Ready for pickup" for pickup-style businesses. */
  pickupPrefs?: CustomerPickupBusinessPrefs;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  lastUpdatedAt?: Date | null;
}

const formatLastUpdated = (date: Date | null | undefined): string => {
  if (!date) return 'Never';
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const CustomerQueueStatusCard: React.FC<CustomerQueueStatusCardProps> = ({
  appointment,
  pickupPrefs,
  onRefresh,
  isRefreshing = false,
  lastUpdatedAt,
}) => {
  const delay = appointment.delayMinutes ?? 0;
  const visitLine = getCustomerQueueStatusLine(appointment, pickupPrefs);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Queue status</Text>
      <Text style={styles.line}>Status: {visitLine}</Text>
      {delay > 0 && (
        <Text style={styles.line}>Note: running about {delay} minutes behind schedule</Text>
      )}
      <View style={styles.footerRow}>
        <Text style={styles.lastUpdatedText}>Updated: {formatLastUpdated(lastUpdatedAt)}</Text>
        {!!onRefresh && (
          <TouchableOpacity
            style={[styles.refreshButton, isRefreshing && styles.refreshButtonDisabled]}
            onPress={onRefresh}
            disabled={isRefreshing}
          >
            <Text style={styles.refreshButtonText}>{isRefreshing ? 'Refreshing...' : 'Refresh'}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    borderRadius: 10,
    backgroundColor: '#EEF6FF',
    borderWidth: 1,
    borderColor: '#CFE5FF',
    padding: 10,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0B4A8B',
    marginBottom: 4,
  },
  line: {
    fontSize: 13,
    color: '#0B4A8B',
    marginBottom: 2,
  },
  footerRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  lastUpdatedText: {
    fontSize: 11,
    color: '#4A6FA5',
  },
  refreshButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#0B7CFF',
  },
  refreshButtonDisabled: {
    opacity: 0.7,
  },
  refreshButtonText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
  },
});

export default CustomerQueueStatusCard;
